import React from 'react';
import moment from 'moment';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DatePicker, RadioButtonGroup } from 'ui-library';
import {
    compareDates,
    dateFormatZurich,
} from 'utils/datetime';
import Error from 'components/AtomicStructure/atoms/Error';
import { TranslationFieldsComponent } from './TranslationFieldsComponent';

const SystemMessageFormFields = ({
    languages,
    activeLang,
    control,
    setError,
    platformOptions,
    startDateValue,
    endDateValue,
    setValue,
    timeRangeError,
    startDateDisabled = false,
}) => {
    const { t } = useTranslation();

    return (
        <div>
            {/* ALL LANGUAGES REGISTERED */}
            {languages.map((lang) => (
                <TranslationFieldsComponent
                    key={lang.value}
                    lang={lang.value}
                    activeLang={activeLang}
                    control={control}
                    setError={setError}
                    setValue={setValue}
                />
            ))}

            <div className="system-message-field-seperator" />

            <div className="system-message-fields--date-row mb-8">
                {/* START DATE */}
                <div className="system-message-fields--date-column">
                    <Controller
                        name="startDate"
                        control={control}
                        render={({
                            fieldState,
                            field: { onChange, value },
                        }) => (
                            <DatePicker
                                disabled={startDateDisabled}
                                format={dateFormatZurich}
                                value={value}
                                error={fieldState?.error?.message}
                                disabledDate={(curr) => {
                                    if (!curr) return false;

                                    // Disable all dates before today
                                    const isBeforeToday = curr
                                        .startOf('day')
                                        .isBefore(moment().startOf('day'));

                                    // Disable dates after end date (+1 day if needed)
                                    const isAfterEndDate = endDateValue
                                        ? curr
                                            .startOf('day')
                                            .isAfter(
                                                moment(endDateValue)
                                                    .startOf('day')
                                                    .add(1, 'day'),
                                            )
                                        : false;

                                    return isBeforeToday || isAfterEndDate;
                                }}
                                onChange={(momentDate) => onChange(momentDate)}
                                required
                                label={t(
                                    'SystemMessage.Form.FieldLabel.StartDate',
                                )}
                            />
                        )}
                    />
                </div>

                {/* END DATE */}
                <div className="system-message-fields--date-column">
                    <Controller
                        name="endDate"
                        control={control}
                        render={({
                            fieldState,
                            field: { onChange, value },
                        }) => (
                            <DatePicker
                                format={dateFormatZurich}
                                value={value}
                                error={fieldState?.error?.message}
                                disabledDate={(current) => current
                                        && compareDates(
                                            moment(
                                                startDateValue,
                                                dateFormatZurich,
                                            ),
                                            current,
                                        )}
                                onChange={(momentDate) => onChange(momentDate)}
                                required
                                label={t(
                                    'SystemMessage.Form.FieldLabel.EndDate',
                                )}
                            />
                        )}
                    />
                </div>
            </div>
            {timeRangeError && (
                <Error className="mb-20">{timeRangeError}</Error>
            )}

            {/* PLATFORM */}
            <fieldset className="system-message-fields--platforms mt-40">
                <h3>
                    {t('SystemMessage.Form.FieldLabel.Platform')}
                    {' '}
                    *
                </h3>
                <Controller
                    name="platform"
                    control={control}
                    render={({ field, fieldState }) => (
                        <RadioButtonGroup
                            {...field}
                            horizontal
                            data-cy="platform"
                            options={platformOptions}
                            error={fieldState?.error?.message}
                            required
                        />
                    )}
                />
            </fieldset>
        </div>
    );
};

export default SystemMessageFormFields;
